import { RefObject, useEffect, useState } from 'react';

import { SurveyConfig } from '@/common/constants/porsline';
import {
  createSurveyRoot,
  createSurveyScript,
  lockScroll,
} from '@/common/utils/survey-dom';

type Params = {
  survey: SurveyConfig | null;
  containerRef: RefObject<HTMLDivElement | null>;
  shouldLockScroll?: boolean;
};

export function useSurveyEmbed({
  survey,
  containerRef,
  shouldLockScroll = true,
}: Params) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!survey || !containerRef.current) return;

    setIsLoading(true);

    const unlockScroll = shouldLockScroll ? lockScroll() : undefined;

    const container = containerRef.current;
    container.replaceChildren();

    const surveyRoot = createSurveyRoot(survey.id);

    surveyRoot.appendChild(createSurveyScript(survey.url));
    container.appendChild(surveyRoot);

    const observer = new MutationObserver(() => {
      const iframe = surveyRoot.querySelector('iframe');

      if (!iframe) return;

      observer.disconnect();

      iframe.addEventListener('load', () => setIsLoading(false), {
        once: true,
      });
    });

    observer.observe(surveyRoot, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      container.replaceChildren();
      unlockScroll?.();
    };
  }, [survey, containerRef, shouldLockScroll]);

  return isLoading;
}
